// Imports
import { Button, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GobikesApi } from "../../config/Apis";
import { Colors, DEFAULT_LOADER_STYLES } from "../../config/Constants";
import { getHubSelection } from "../../features/hubSelection";
import { getAuthCookie } from "../auth/AuthUtil";
import { nonNegativeValidationFn, parseFalsyFields } from "../utils/ValidationUtils";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { DashboardLoader } from "../utils/LocalStateUtils";
import { AccessoryModel, CouponModel, OfferType } from "../utils/Models";
import { Close, DoneAll } from "@mui/icons-material";

// Constants and interfaces
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  accessoryClassData?: AccessoryModel[]
  accessoryClassMap?: { [k: number]: AccessoryModel }
}

// Individual booking details
export function OfferDetails(props: {
  offer: CouponModel,
  backFn: (reload: boolean) => void,
  drawerWidth: number,
  editMode: boolean,
  creating: boolean
}) {

  // Local states
  const [editMode, setEditMode] = useState(props.editMode);
  const [state, setState] = useState<LocalState>({});
  const [currentOfferData, setCurrentOfferData] = useState({ offer: { ...props.offer }, error: '' as string | undefined });

  // Parse state and props
  const authCookie = getAuthCookie();
  const selectedHub = useSelector(getHubSelection);
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Load data on when component opens if creating
  useEffect(() => {
    if (!props.creating || state.accessoryClassData) return;
    setState({ loading: true });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/bikes/accessories?hubId=${selectedHub.id}`, { headers: { 'vendor-token': authCookie } });
        if (!data.data.accessories) {
          throw new Error('Failed to load bike class data!');
        }
        setState({ loading: false, accessoryClassData: data.data.accessories, accessoryClassMap: data.data.accessories.reduce((a: { [k: number]: AccessoryModel }, v: AccessoryModel) => ({ ...a, [v.id]: v }), {}) });
      } catch (e: any) {
        console.log(e)
        setState({ loading: false, error: true, message: e.message });
      }
    })();
  }, [props]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset current offer data when edit mode changed
  useEffect(() => {
    if (!editMode) setCurrentOfferData({ ...currentOfferData, offer: { ...props.offer } });
  }, [editMode, props]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to update current offer state
  function updateCurrentOfferData(update: Partial<CouponModel>) {
    setCurrentOfferData({ ...currentOfferData, offer: { ...currentOfferData.offer, ...update } })
  }

  // Save offer details
  async function saveOfferDetails() {

    // Parse input and check for errors
    const payload = currentOfferData.offer;
    if (payload.minDays < 0) return setCurrentOfferData({ ...currentOfferData, error: 'Min duration should be a positive number!' });
    if (payload.minOrder < 0) return setCurrentOfferData({ ...currentOfferData, error: 'Min duration should be a positive number!' });
    if (payload.offerType === OfferType.PERCENT && (!payload.maxAmount || payload.maxAmount < 0)) return setCurrentOfferData({ ...currentOfferData, error: 'Max amount should be a positive number!' });
    const invalidFields = parseFalsyFields([
      { name: 'Code', value: payload.code },
      { name: 'Title', value: payload.title },
      { name: 'Description', value: payload.description },
      { name: 'Start date', value: payload.offerStart },
      { name: 'End date', value: payload.offerEnd },
      { name: 'Offer Type', value: payload.offerType },
      { name: 'Offer Value', value: payload.value },
    ]);
    if (invalidFields.length) {
      setCurrentOfferData({ ...currentOfferData, error: `Please enter these required fields: ${invalidFields.join(', ')}` });
      return;
    }
    if (payload.offerType === OfferType.PERCENT && payload.value) payload.value = payload.value / 100;
    if (payload.offerType === OfferType.FREE_ACCSSORY && payload.value) payload.accessoryId = payload.value;

    // Set loading mode and try to save offer
    setState({ loading: true });
    try {
      await GobikesApi.post(`/vendors/coupons`, payload, { headers: { 'vendor-token': authCookie } });
      setState({ error: false, message: 'Offer created successfully!', loading: false })
      props.backFn(true);
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setState({ error: true, message: errorMessage, loading: false })
    }
  }

  // Return DOM
  if (state.error || state.loading)
    return (
      <DashboardLoader
        {...state}
        customIcon={<DoneAll className="icon" style={{ color: Colors.primary }} />}
        resetError={state.error ? () => setState({ ...state, error: false, loading: false }) : undefined}
        resetErrorText={'Dismiss'}
        resetErrorIcon={<Close />}
        resetErrorIconPrefix={true}
        styles={DEFAULT_LOADER_STYLES}
      />
    )
  else return (
    <Fragment>
      <div className="gbk-paper-container">

        {/* Empty space at top for phone screen */}
        <Fragment>
          <Box sx={{ display: { xs: 'block', md: 'none' }, height: { xs: '18px', sm: '26px' }, width: 1 }}><br /></Box>
        </Fragment>

        {/* Coupon details */}
        {renderOfferDetails()}<br /><br />

        {/* Discount details */}
        {renderOfferConstraints()}<br /><br />

        <br />
      </div>

      {/* Action buttons */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: 0, md: props.drawerWidth },
        width: { xs: '100vw', md: `calc(100vw - ${props.drawerWidth}px)` },
        padding: '20px 40px',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        background: '#fff',
        boxShadow: '0px -2px 6px #ddd',
        zIndex: 10
      }}>
        {
          props.creating ? (
            <Fragment>
              <Button className="thick-border" variant="contained" onClick={saveOfferDetails}>Create</Button>
              <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => props.backFn(true)}>Cancel</Button>
              <Typography className='gbk-message error' sx={{ display: 'inline-block', paddingLeft: isPhone ? '0px' : '20px', width: isPhone && currentOfferData.error ? 'calc(100% - 80px)' : 'auto', paddingTop: isPhone && currentOfferData.error ? '10px' : '0px' }}> {currentOfferData.error} </Typography>
            </Fragment>
          ) : (
            editMode
              ? (
                <Fragment>
                  <Button className="thick-border" variant="contained" onClick={saveOfferDetails}>Save</Button>
                  <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => setEditMode(false)}>Cancel</Button>
                </Fragment>
              )
              : (
                <Fragment>
                  <Button className="thick-border" variant="contained" onClick={e => setEditMode(true)}>Edit</Button>
                </Fragment>
              )
          )
        }
      </Box>
    </Fragment >
  )

  /*
   * Render utility functions beyond this point 
   */

  // High level offer details
  function renderOfferDetails() {
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Offer Details</Typography>
      </div>
      <div className="content" >
        <div className="gbk-col" style={{ minWidth: 200 }}>
          <GobikesField
            type={GbkFieldType.STRING}
            label={'Offer Code'}
            editMode={props.creating || editMode}
            value={currentOfferData.offer.code}
            updateFn={(v: string) => {
              updateCurrentOfferData({ code: v })
            }}
          />
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 400 }}>
          <GobikesField
            type={GbkFieldType.STRING}
            label={'Title'}
            editMode={props.creating || editMode}
            value={currentOfferData.offer.title || ''}
            updateFn={(v: string) => {
              updateCurrentOfferData({ title: v })
            }}
          />
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 400 }}>
          <GobikesField
            type={GbkFieldType.STRING}
            label={'Description'}
            editMode={props.creating || editMode}
            value={currentOfferData.offer.description || ''}
            updateFn={(v: string) => {
              updateCurrentOfferData({ description: v })
            }}
          />
          <br />
        </div>
      </div>
    </Paper>
  }

  // Offer constraints
  function renderOfferConstraints() {
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Offer Constraints</Typography>
      </div>
      <div className="content" >
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.DATE_TIME}
            label={'Start date'}
            editMode={true}
            value={currentOfferData.offer.offerStart ? DateTime.fromMillis(currentOfferData.offer.offerStart) : null}
            updateFn={v => { updateCurrentOfferData({ offerStart: v }) }}
          />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.DATE_TIME}
            label={'End date'}
            editMode={true}
            value={currentOfferData.offer.offerEnd ? DateTime.fromMillis(currentOfferData.offer.offerEnd) : null}
            updateFn={v => { updateCurrentOfferData({ offerEnd: v }) }}
          />
        </div> <br />
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.SELECT}
            label={'Offer Type'}
            editMode={editMode}
            selectOptions={[OfferType.FLAT, OfferType.PERCENT, OfferType.FREE_ACCSSORY, OfferType.FREE_KMS].map(a => ({ display: a, value: a.toString() }))}
            value={currentOfferData.offer.offerType || ''}
            updateFn={(v: OfferType) => {
              updateCurrentOfferData({ offerType: v })
            }}
          />
          <br /><br />
        </div>
        {currentOfferData.offer.offerType ? (
          currentOfferData.offer.offerType === OfferType.FREE_ACCSSORY ? (
            <div className="gbk-col" style={{ minWidth: 400 }}>
              <GobikesField
                type={GbkFieldType.SELECT}
                label={'Accessory'}
                editMode={props.creating || editMode}
                value={currentOfferData.offer.value || ''}
                selectOptions={(state.accessoryClassData || []).map(a => ({ value: a.id, display: a.name }))}
                updateFn={(v: number) => {
                  updateCurrentOfferData({ value: v })
                }}
              />
              <br /><br />
            </div>
          ) : (
            <div className="gbk-col" style={{ minWidth: 300 }}>
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={`Value ${currentOfferData.offer.offerType === OfferType.FLAT ? '(₹)' : ''}`}
                editMode={props.creating || editMode}
                value={currentOfferData.offer.value || ''}
                updateFn={(v: number) => {
                  updateCurrentOfferData({ value: v })
                }}
                validateFn={nonNegativeValidationFn()}
              />
              <br />
            </div>
          )
        ) : <br />}
        {currentOfferData.offer.offerType === OfferType.PERCENT ? (
          <Fragment>
            <div className="gbk-col" style={{ minWidth: 300 }}>
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={`Max Discount (₹)`}
                editMode={props.creating || editMode}
                value={currentOfferData.offer.maxAmount || ''}
                updateFn={(v: number) => {
                  updateCurrentOfferData({ maxAmount: v })
                }}
                validateFn={nonNegativeValidationFn()}
              />
              <br />
            </div>
            <br />
          </Fragment>
        ) : <br />}

        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Min. order amount (₹)'}
            editMode={true}
            value={currentOfferData.offer.minOrder || 0}
            updateFn={v => { updateCurrentOfferData({ minOrder: v }) }}
            validateFn={nonNegativeValidationFn()}
          />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Min. order duration (days)'}
            editMode={true}
            value={currentOfferData.offer.minDays || 0}
            updateFn={v => { updateCurrentOfferData({ minDays: v }) }}
            validateFn={nonNegativeValidationFn()}
          />
        </div> <br />
      </div>
    </Paper>
  }

}
